// @ts-nocheck

import React from 'react';
import styled, { css } from 'styled-components';

import { Cross } from '../../assets/svg/common';
import { CARD_BADGE_WIDTH_BASKET, CARD_BADGE_HEIGHT_BASKET, CARD_BADGE_WIDTH } from '../../constants/ui/cardBadge';
import theme from '../../styled-components/dark';
import { type CardMode } from '../../types/common';
import { SportIds } from '../../types/sports';
import MatchCardHeaderBadge from '../MatchCardHeaderBadge';
import { name as FullMatchTitleClassName } from '../MatchFullTitle';

const HEADER_HEIGHT = 1.4;
const CARD_BADGE_LEFT_SHIFT = 0.45;
const CARD_BADGE_WIDTH_BOOSTED_ODDS = CARD_BADGE_WIDTH * 1.4; // Resize badge for boosted odds

const Wrapper = styled.div`
  position: relative;

  z-index: 1;
  width: 100%;
  height: ${HEADER_HEIGHT}em;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;

  display: flex;
  justify-content: center;
  align-items: center;
  background: ${theme.MatchCardHeader.basketBackgroundColor};

  user-select: none;
  -webkit-tap-highlight-color: transparent;

  ~ .${FullMatchTitleClassName} {
    padding-top: ${CARD_BADGE_HEIGHT_BASKET - HEADER_HEIGHT + 0.1}em;
  }
`;

const StyledMatchCardHeaderBadge = styled(MatchCardHeaderBadge)`
  transform: ${({ hasAttach }) => (hasAttach ? `translateX(-${CARD_BADGE_LEFT_SHIFT}em)` : undefined)};
  position: absolute;
  top: 0;
  left: 0;
  height: ${CARD_BADGE_HEIGHT_BASKET}em;
  width: ${CARD_BADGE_WIDTH_BASKET}em;

  ${({ $cardMode, sportId }) => {
    if (sportId === SportIds.SPECIAL_ODDS) {
      return css`
        width: ${CARD_BADGE_WIDTH_BOOSTED_ODDS}em;
        && .badge {
          margin-left: 2px;
          background-image: ${theme.BoostedOddCard.badge[$cardMode]?.backgroundImage};
          box-shadow:
            inset 0 0 0 1px black,
            0.12em 0.12em 0.4em 0 rgb(0 0 0 / 80%);
          box-sizing: border-box;
          border: solid 1px ${theme.BoostedOddCard.badge[$cardMode]?.color};
        }
        && .boostedIcon {
          color: ${theme.BoostedOddCard.badge[$cardMode]?.iconColor};
        }
      `;
    }
  }}
`;

const LeftColumn = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;
  font-size: 0.9em;
`;

const CenterColumn = styled.div`
  flex: ${({ $sportId }) => ($sportId === SportIds.SPECIAL_ODDS ? 2.5 : 4)};
  height: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Title = styled.div`
  color: ${theme.MatchCardHeader.Timer.color};
  font-weight: 400;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 1em;
`;

const RightColumn = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const CloseIcon = styled(Cross)`
  width: 0.8em;
  height: 0.8em;
  margin: 0 10px;
  fill: ${theme.BasketCard.closeButtonColor};
  cursor: pointer;
`;

type BasketCardHeaderProps = {
  title: string;
  sportId: number;
  categoryId: number;
  cardBadgeHasAttach?: boolean;
  isLive: boolean;
  className?: string;
  cardMode: CardMode;
  onClickClose: (...params: unknown) => unknown;
};

const BasketCardHeader = ({
  title,
  sportId,
  categoryId,
  cardBadgeHasAttach = false,
  isLive,
  onClickClose,
  className,
  cardMode,
}: BasketCardHeaderProps) => {
  return (
    <Wrapper className={className}>
      <LeftColumn isSticked={!cardBadgeHasAttach}>
        <StyledMatchCardHeaderBadge
          sportId={sportId}
          categoryId={categoryId}
          hasAttach={cardBadgeHasAttach}
          isLive={isLive}
          $cardMode={cardMode}
        />
      </LeftColumn>
      <CenterColumn $sportId={sportId}>
        <Title>{title}</Title>
      </CenterColumn>
      <RightColumn>{onClickClose ? <CloseIcon onClick={onClickClose} /> : null}</RightColumn>
    </Wrapper>
  );
};

export default React.memo<BasketCardHeaderProps>(BasketCardHeader);
